import { useTranslation } from 'react-i18next';
import {
  ChangeEvent, FC, MouseEvent, useMemo, useState,
} from 'react';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BASE_COLORS, generateURL } from '@mgm/common/utils';
import { TDropzoneFile } from './type';

interface DragEvent<T = Element> extends MouseEvent<T> {
  dataTransfer: DataTransfer;
}

const DropzoneFile: FC<TDropzoneFile> = ({ setAvatarFile, avatarFile }) => {
  const { t } = useTranslation('settings');
  const theme = useTheme() as TCustomTheme;
  const { isLight } = useCustomTheme(theme);

  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setAvatarFile(e.dataTransfer.files[0]);
    }
  };
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (e.target.files && e.target.files[0]) {
      setAvatarFile(e.target.files[0]);
    }
  };

  const deleteAvatar = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setAvatarFile({} as File);
  };

  const dropzoneStyled = useMemo(() => ({
    backgroundColor: isLight ? '#f7fafc' : 'rgba(145, 158, 171, 0.16)',
    border: isLight ? '1px dashed #e2e8f0' : '1px dashed rgba(145, 158, 171, 0.32)',
  }), [isLight]);

  return (
    <form id="form-file-upload" onDragEnter={handleDrag as any} onSubmit={(e) => e.preventDefault()}>
      <input key={avatarFile.name} accept=".pdf,.jpg,.jpeg,.png" type="file" id="input-file-upload" onChange={handleChange} />
      <label
        id="label-file-upload"
        htmlFor="input-file-upload"
        style={dropzoneStyled}
        className={dragActive ? 'drag-active' : ''}
      >
        <Typography fontSize={12} color={BASE_COLORS.primary}>
          { t('dropFiles') }
          <u className="px-1">{ t('clickHere') }</u>
          {t('toUpload')}
        </Typography>
        { avatarFile?.name && (
        <Chip
          sx={{
            mt: 2,
          }}
          color="primary"
          onDelete={deleteAvatar}
          avatar={<Avatar src={generateURL(avatarFile)} />}
          label={avatarFile.name}
          variant="outlined"
        />
        )}
      </label>
      { dragActive
        && (
        <div
          id="drag-file-element"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        />
        ) }
    </form>
  );
};

export {
  DropzoneFile,
};
